import React, { useEffect, useState } from "react"
import ModalComponent from "../../components/Modal"
import { Col, FloatingLabel, Form, Row, Spinner, Button } from "react-bootstrap"
import CustomSelect from "../../components/CustomSelect"
import { Map } from "@vis.gl/react-google-maps"
import { useFormik } from "formik"
import { toast } from "react-toastify"
import * as Yup from "yup"
import { admin } from "../../config/apiEndpoints"
import axiosProvider from "../../lib/AxiosProvider"
import {
  GetUserToken,
  handleOnBlur,
  handleOnchange,
} from "../../lib/GlobalMethods"
import { _latitudeRegex_, _longitudeRegex_ } from "../../lib/Regex"
import axios from "axios"

const ExportingStationData = ({
  handleClose,
  show,
  setShow,
  chargingStationId,
  fleetClientId,
}) => {
  // const [marker, setMarker] = useState({
  //     lat: 23.0042882,
  //     lng: 72.500343,
  //     zoom: 16,
  // })
  const [loading, setLoading] = useState(false)
  const [fleetVehicleData, setFleetVehicleData] = useState([])
  const token = GetUserToken()

  useEffect(() => {
    const fetchFleetVehicleData = async () => {
      try {
        const response = await axiosProvider({
          method: "GET",
          endpoint: `${admin.fleetVehicleList}?page=1&limit=200`,
        })

        if (response?.data?.code === 200) {
          setFleetVehicleData(
            (response?.data?.data || [])
              .map((vehicle) => ({
                value: vehicle?.id,
                label: `${vehicle?.id} - ${vehicle?.registration_number}`,
              }))
              .filter(
                (vehicle) =>
                  vehicle.value !== undefined && vehicle.label !== undefined,
              ),
          )
        }
      } catch (error) {
        toast.error(error?.message || "Unable to fetch fleet vehicles")
      }
    }

    fetchFleetVehicleData()
  }, [])

  const validationSchema = Yup.object({
    startDate: Yup.string().required("Start date is required"),
    endDate: Yup.string().required("End date is required"),
  })

  const getExportErrorMessage = async (error) => {
    const errorData = error?.response?.data || error?.data

    if (errorData instanceof Blob) {
      try {
        const json = JSON.parse(await errorData.text())
        return json?.message || "No booking data found for the selected filters"
      } catch (parseError) {
        return "No booking data found for the selected filters"
      }
    }

    return (
      errorData?.message || error?.message || "Unable to export booking data"
    )
  }

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setLoading(true)
      const exportParams = new URLSearchParams({
        export: "csv",
        from_date: values.startDate,
        to_date: values.endDate,
        station_id: chargingStationId,
      })

      if (fleetClientId) {
        exportParams.set("fleet_client_id", fleetClientId)
      }

      if (values.fleetVehicleId) {
        exportParams.set("fleet_vehicle_id", values.fleetVehicleId)
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}${admin.bookingList}?${exportParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "User-Type": "Admin",
          },
          responseType: "blob",
        },
      )

      const contentType = response?.headers["content-type"]

      if (contentType && contentType.includes("application/json")) {
        const text = await response?.data?.text()
        const json = JSON.parse(text)
        toast.error(
          json?.message || "No booking data found for the selected filters",
        )
        return
      }

      if (contentType && contentType.includes("text/csv")) {
        const blob = new Blob([response.data], {
          type: "text/csv;charset=utf-8;",
        })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", "booking-list.csv")
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
        toast.success("File downloaded successfully")
        resetForm()
        handleClose()
      } else {
        toast.error("Unable to export booking data")
      }
    } catch (errors) {
      toast.error(await getExportErrorMessage(errors))
    } finally {
      setLoading(false)
    }
  }

  const formik = useFormik({
    initialValues: {
      startDate: "",
      endDate: "",
      fleetVehicleId: "",
    },
    validationSchema,
    onSubmit: handleSubmit,
    enableReinitialize: true,
  })

  const { errors, values, touched, resetForm, setFieldValue } = formik

  return (
    <ModalComponent
      show={show}
      onHide={() => {
        handleClose()
        resetForm()
      }}
      modalsize={"lg"}
      backdrop="static"
      animationFade={true}
      modeltitle={"Export CSV"}
      submitname={
        loading ? (
          <Spinner size="sm" animation="border" variant="white" />
        ) : (
          "Export"
        )
      }
      btnclosetext={"Cancel"}
      handleSubmit={formik.handleSubmit}
      children={
        <>
          <Form noValidate>
            <Row xs={"auto"}>
              <Col xs={12}>
                <CustomSelect
                  options={fleetVehicleData}
                  handleSelectChange={(selectedFleetVehicle) =>
                    setFieldValue(
                      "fleetVehicleId",
                      selectedFleetVehicle ? selectedFleetVehicle.value : "",
                    )
                  }
                  isMulti={false}
                  isSearchable={true}
                  placeholder={"Select Fleet Vehicle (Optional)"}
                  classNameSelect={"selectdropdown_filter mb-3"}
                />
              </Col>
              <Col xs={6}>
                <FloatingLabel
                  controlId="station_name"
                  label={
                    <>
                      <span>Start Date</span>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="date"
                    name="startDate"
                    value={values.startDate}
                    onChange={(e) =>
                      formik.setFieldValue("startDate", e.target.value)
                    }
                    onBlur={() => formik.setFieldTouched("startDate", true)}
                    isInvalid={touched.startDate && !!errors.startDate}
                    isValid={touched.startDate && !errors.startDate}
                    lang="en-GB"
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.startDate}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
              <Col xs={6}>
                <FloatingLabel
                  controlId="endDate"
                  label={
                    <>
                      <span>End Date</span>
                      <span className="required_star">*</span>
                    </>
                  }
                  className="mb-3"
                >
                  <Form.Control
                    type="date"
                    name="endDate"
                    value={values.endDate}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) =>
                      formik.setFieldValue("endDate", e.target.value)
                    }
                    onBlur={() => formik.setFieldTouched("endDate", true)}
                    isInvalid={touched.endDate && !!errors.endDate}
                    isValid={touched.endDate && !errors.endDate}
                    lang="en-GB"
                  />

                  <Form.Control.Feedback type="invalid">
                    {errors.endDate}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>
            </Row>
          </Form>
        </>
      }
    />
  )
}

export default ExportingStationData
